import { bodycamClient, rmsClient } from "@/shared/api/clients";
import { VideoData } from "@/shared/interfaces/VideoData";
import { EforceIncidentModel } from "@/shared/interfaces/EforceModels";

export interface ReportMetadataRecord {
  stationId: string;
  reportId: string;
  userId: string;
  rms: string;
  reportStatus: string;
  reportDescription?: string | null;
  reportModifiedById: string;
  reportSignedById?: string | null;
  signedAt?: string | null;
  rmsCommitById?: string | null;
  rmsCommitAt?: string | null;
  evidenceIds: string[];
  promptTokenUsage?: number;
  completionTokenUsage?: number;
  createdAt: string;
  updatedAt: string;
  narrativeTemplateId?: string | null;
  statusMessage?: string;
  progress?: number;
  attemptNumber?: number;
  // Server-set on POST /reports/{id}/review (REPORT_REVIEW_API_REFERENCE.md §5.4).
  // reviewedAt is the timestamp of the latest CO action (approve or reject)
  // and is cleared on retract. reportStatus disambiguates approve vs reject.
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  reviewedAt?: string | null;
}

interface ReportMetadataResponse extends ReportMetadataRecord {
  canEdit: boolean;
  // Server flag for the narrative-only workflow: true while the report has only
  // a narrative (the full schema-bound report has not been generated yet).
  // Absent on legacy reports — those are treated as full reports.
  narrativeOnly?: boolean;
}

export const getReportMetadata = async (
  reportId: string,
): Promise<ReportMetadataResponse> => {
  const response = await rmsClient.get<ReportMetadataResponse>("/report", {
    params: { reportId },
  });

  return response.data;
};

/**
 * A report is "narrative-only" (full report not yet generated) when the server
 * explicitly says so. Centralized so the field name lives in one place. Absent
 * flag ⇒ full report (back-compat with reports created before this workflow).
 */
export const isNarrativeOnly = (m: { narrativeOnly?: boolean }): boolean =>
  m.narrativeOnly === true;

export const getEvidenceData = async (
  evidenceId: string,
): Promise<VideoData> => {
  const response = await bodycamClient.get<VideoData>("/evidence/details", {
    params: { evidenceId },
  });

  return response.data;
};

/**
 * The /artifact response: a stored report record. `reportContent` is the
 * incident body (the editor/store treat it as the eForce-shaped model). The
 * server returns more fields on the wire; only those consumed here are typed.
 */
export interface IncidentData {
  reportContent: EforceIncidentModel;
  reportType: string;
  versionNumber?: number;
  updatedAt?: string;
}

export const getIncidentData = async (
  reportId: string,
  versionNumber?: number,
): Promise<IncidentData> => {
  // Editor, review, and version-history reads need the stored citation bytes.
  // Fetch them even while the display flag is off: a plain-text response would
  // make an unrelated edit silently delete the report's existing provenance.
  const params: Record<string, string | number> = {
    reportId,
    citations: "include",
  };
  if (typeof versionNumber === "number") params.version = versionNumber;
  const response = await rmsClient.get<IncidentData>("/artifact", { params });

  return response.data;
};

/**
 * The narrative-only counterpart to /artifact: when a report has only a
 * narrative (metadata.narrativeOnly === true) there is no schema-bound
 * artifact, so the editor reads the plain-text narrative from here instead.
 * 404 once the report has no narrative; 403 on station mismatch.
 */
export interface ReportNarrativeData {
  stationId: string;
  reportId: string;
  narrative: string;
  versionNumber: number;
  reportType?: string;
  createdAt: string;
  updatedAt: string;
}

export const getReportNarrative = async (
  reportId: string,
  version?: number,
): Promise<ReportNarrativeData> => {
  // Same preservation contract as getIncidentData; the UI flag controls how
  // these markers render, independently of whether edits must retain them.
  const params: Record<string, string | number> = {
    reportId,
    citations: "include",
  };
  if (typeof version === "number") params.version = version;
  const response = await rmsClient.get<ReportNarrativeData>(
    "/report-narrative",
    { params },
  );
  return response.data;
};

/**
 * Builds the minimal incident model used to render a narrative-only report
 * (which has no schema-bound artifact). The narrative prose is stored under
 * `Text` (eForce) or `Narrative` (KAR); key it by RMS so readers like
 * extractNarrativeTexts and the editor resolve it consistently.
 */
export const buildNarrativeIncident = (
  narrative: string,
  rms: string | null,
): EforceIncidentModel =>
  ({
    Narratives: [{ [rms === "EF" ? "Text" : "Narrative"]: narrative }],
  }) as unknown as EforceIncidentModel;

export const deleteReport = async (reportId: string): Promise<void> => {
  await rmsClient.delete("/report", { params: { reportId } });
};

export const signReport = async (reportId: string): Promise<void> => {
  await rmsClient.post("/report/sign", { reportId });
};

export const rmsCommitReport = async (
  reportId: string | number,
): Promise<{ eventNumber?: string }> => {
  const res = await rmsClient.post("/report/rms-commit", { reportId });
  return res.data ?? {};
};
